///<reference lib="dom"/>
/** @jsx h */
/** @jsxFrag Fragment */
import { Fragment, h } from "preact";
import { useCallback, useEffect, useState } from "preact/hooks";
import type { SwCacheSummary, SwInfo } from "../protocol.tsx";
import {
  SwApplyUpdate,
  SwFetchVersion,
  SwInfoQuery,
  SwPersist,
  SwRegister,
  SwReset,
  SwScopeCaches,
  SwStorage,
  type SwVersion,
  SwWatch,
} from "../client/client.tsx";

export interface UseSwProps {
  app: string;
  release: string;
  enabled: boolean;
}
export function useSw(props: UseSwProps) {
  const [reg, setReg] = useState<ServiceWorkerRegistration | null>(null);
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stop = () => {};
    SwRegister({ enabled: props.enabled })
      .then((r) => {
        setReg(r);
        if (r) {
          stop = SwWatch({ reg: r, onWaiting: setWaiting });
          r.update().catch(() => {});
        }
      })
      .catch((e) => setError(String(e)));
    return () => stop();
  }, [props.enabled]);

  const apply = useCallback(() => {
    if (reg) SwApplyUpdate(reg);
  }, [reg]);

  const check = useCallback(async () => {
    if (reg) await reg.update().catch(() => {});
    return await SwFetchVersion(props.release);
  }, [reg, props.release]);

  return { ...props, reg, waiting, error, apply, check };
}
export type SwHandle = ReturnType<typeof useSw>;

export interface SwUpdateBannerProps {
  sw: SwHandle;
}
export function SwUpdateBanner(props: SwUpdateBannerProps) {
  if (!props.sw.waiting) return null;
  return (
    <div className="sw-banner" role="status">
      <span>Update available</span>
      <button type="button" onClick={props.sw.apply}>reload</button>
    </div>
  );
}

function kb(n: number) {
  return `${Math.round(n / 1024).toLocaleString()} kB`;
}

export interface SwManagerProps {
  sw: SwHandle;
}
export function SwManager(props: SwManagerProps) {
  const { sw } = props;
  const [info, setInfo] = useState<SwInfo | null>(null);
  const [latest, setLatest] = useState<SwVersion | null>(null);
  const [cacheList, setCacheList] = useState<SwCacheSummary[]>([]);
  const [storage, setStorage] = useState<
    { usage: number; quota: number; persisted: boolean } | null
  >(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setCacheList(await SwScopeCaches(sw.app).catch(() => []));
    setStorage(await SwStorage().catch(() => null));
    setLatest(await SwFetchVersion(sw.release));
    if (sw.reg) setInfo(await SwInfoQuery(sw.reg).catch(() => null));
  }, [sw.reg, sw.app, sw.release]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const run = (fn: () => Promise<unknown>) => async () => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const state = sw.reg
    ? [sw.reg.active && "active", sw.reg.waiting && "waiting", sw.reg.installing && "installing"]
      .filter(Boolean).join(", ") || "registered"
    : sw.enabled
    ? "not registered"
    : "disabled";

  return (
    <div className="sw-manager">
      <table>
        <tbody>
          <tr>
            <th>running</th>
            <td>{sw.release}</td>
          </tr>
          <tr>
            <th>latest</th>
            <td>{latest?.release ?? "?"}</td>
          </tr>
          <tr>
            <th>worker</th>
            <td>{state}{info ? ` (${info.release})` : ""}</td>
          </tr>
          <tr>
            <th>storage</th>
            <td>{storage ? `${kb(storage.usage)} / ${kb(storage.quota)}` : "?"}</td>
          </tr>
          <tr>
            <th>persistent</th>
            <td>{storage ? String(storage.persisted) : "?"}</td>
          </tr>
          {sw.error && (
            <tr>
              <th>error</th>
              <td>{sw.error}</td>
            </tr>
          )}
        </tbody>
      </table>
      <ul>
        {cacheList.map((c) => (
          <li key={c.name}>{c.name.split(":").slice(-2).join(":")} — {c.entries}</li>
        ))}
      </ul>
      <div className="sw-actions">
        <button type="button" disabled={busy} onClick={run(sw.check)}>check for update</button>
        {sw.waiting && <button type="button" onClick={sw.apply}>apply update</button>}
        <button type="button" disabled={busy} onClick={run(SwPersist)}>persist storage</button>
        <button type="button" disabled={busy} onClick={run(() => SwReset({ app: sw.app }))}>
          reset
        </button>
      </div>
    </div>
  );
}

export interface SwResetPageProps {
  title: string;
}
export function SwResetPage(props: SwResetPageProps) {
  return (
    <>
      <h1>{props.title}: reset</h1>
      <p>
        Clears this app's offline caches and removes its service worker, then reloads. Saved
        progress is not touched.
      </p>
      <p data-sw-status=""></p>
      <button type="button" data-sw-reset="">reset and reload</button>
    </>
  );
}
